import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Toast, ToastType } from '@/types'

export const useUiStore = defineStore('ui', () => {
  // ============================================
  // State
  // ============================================

  const toasts = ref<Toast[]>([])
  const nextToastId = ref(1)

  // Default duration in ms
  const DEFAULT_DURATION = 4000

  // ============================================
  // Getters
  // ============================================

  const activeToasts = computed(() => toasts.value)

  const hasToasts = computed(() => toasts.value.length > 0)

  // ============================================
  // Actions
  // ============================================

  function showToast(
    type: ToastType,
    message: string,
    duration: number = DEFAULT_DURATION
  ): string {
    const id = `toast-${nextToastId.value++}`

    const toast: Toast = {
      id,
      type,
      message,
      duration
    }

    toasts.value.push(toast)
    console.log(`[UiStore] Toast shown: ${type} - ${message}`)

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        hideToast(id)
      }, duration)
    }

    return id
  }

  function hideToast(id: string): void {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
      console.log(`[UiStore] Toast hidden: ${id}`)
    }
  }

  function clearAllToasts(): void {
    toasts.value = []
    console.log('[UiStore] All toasts cleared')
  }

  // Convenience methods
  function success(message: string, duration?: number): string {
    return showToast('success', message, duration)
  }

  function error(message: string, duration?: number): string {
    return showToast('error', message, duration)
  }

  function warning(message: string, duration?: number): string {
    return showToast('warning', message, duration)
  }

  function info(message: string, duration?: number): string {
    return showToast('info', message, duration)
  }

  return {
    // State
    toasts,

    // Getters
    activeToasts,
    hasToasts,

    // Actions
    showToast,
    hideToast,
    clearAllToasts,
    success,
    error,
    warning,
    info
  }
})
